import { useChat } from '@ai-sdk/react';
import { MCPBridgeChatLocator, type BridgeChatRunner } from '../FloatingUIContainer/MCPBridge/MCPBridgeChatLocator';
import { MCPBridgeChatSession } from '../FloatingUIContainer/MCPBridge/MCPBridgeChatSession';
import { useMCPBridgeChat } from '../FloatingUIContainer/MCPBridge/useMCPBridgeChat';
import {
  DefaultChatTransport,
  isToolUIPart,
  lastAssistantMessageIsCompleteWithToolCalls,
  type ChatStatus,
  type FileUIPart,
  type UIMessage,
} from 'ai';
import { nanoid } from 'nanoid';
import { useState, useMemo, type CSSProperties, useRef, useEffect, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import type { ParseKeys } from 'i18next';
import { createViewModelContext } from './createAIViewModelContext';
import {
  AIViewModelContextSchema,
  RTDBPath,
  type ViewJSON,
  AIAssistantClientToolDefs,
  AIAssistantToolNames,
  DEFAULT_AI_ASSISTANT_MODE,
  isAIAssistantMode,
  type AIAssistantClientSkill,
  type AIAssistantSkillCommandMetadata,
  type AIAssistantMode,
  getSelectableAIModels,
  DEFAULT_AI_MODEL_ID,
  isAIModelId,
  type GroupId,
  type ViewId,
  type ViewModelId,
  type AIViewModelContextView,
  extractId,
  omitNullFields,
  normalizeAIEditLinkThemeColors,
  formatAILinkThemeColorReplacements,
  type AILinkThemeColorReplacement,
  StickyNodeSizeByModelingFormat,
  AI_USAGE_LIMIT_EXCEEDED_MESSAGE,
  AI_EXPORT_TEXT_MAX_CHARS,
} from 'balus-common';
import {
  describeViewsOutsideCanvas,
  evaluateEditForApply,
  evaluatePatchForApply,
  formatApplyEvaluation,
  type ApplyEvaluation,
} from './viewQualityChecks';
import { toJsonSchema } from '@valibot/to-json-schema';
import { useSnapshot } from '@framework/hooks/useSnapshot';
import { isDebugMode } from '@framework/utils/WithDebugMode';
import { useHandler, usePopupRef } from '@framework/hooks';
import { ViewEntity } from '@view-model/domain/view/ViewEntity';
import { faChevronLeft, faEllipsisV, faPaperclip, faPlus } from '@fortawesome/free-solid-svg-icons';
import { faTrashAlt } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { classNames } from '@framework/utils/classNames';
import { DarkTooltip } from '@framework/ui/atoms/Tooltip';
import { AIAssistantSettingsStorage } from './AIAssistantSettingsStorage';
import { buildAISystemMessage } from './buildAISystemMessage';
import { ModeSelect } from './ModeSelect';
import { CustomSelect, type CustomSelectOption } from './CustomSelect';
import { AIChatMessageRow } from './AIChatMessageRow';
import { formatToolInputValidationError } from './formatToolInputValidationError';
import { AIEditToolFailureGuard } from './AIEditToolFailureGuard';
import { CHAT_STREAMING_THROTTLE_MS } from './chatStreamingThrottle';
import { useAIAssistantOperationContext } from '../AIAssistantOperationContext';
import { useAtomValue, useSetAtom, useStore } from 'jotai/react';
import { aiWorkingViewIdsAtom } from '../AIModelingEffect/aiWorkingViewIdsAtom';
import { extractAIWorkingViewIds } from '../AIModelingEffect/extractAIWorkingViewIds';
import {
  collectAIModelingEffectElementIds,
  type AIModelingEffectSource,
} from '../AIModelingEffect/collectAIModelingEffectElementIds';
import { runAIModelingAppearEffect } from '../AIModelingEffect/runAIModelingAppearEffect';
import { viewModelPageContentAtom } from '../viewModelPageContentAtom';
import { viewModelModelingFormatAtom } from '../ViewModelModelingFormatAtom';
import * as v from 'valibot';
import { LoadingIndicator } from './LoadingIndicator';
import { useStickToBottomScroll } from './useStickToBottomScroll';
import {
  getViewModelAIAssistantSkills,
  subscribeViewModelAIAssistantSkills,
} from '@view-model/application/ai/ViewModelAIAssistantSkillSource';
import { useRequiredCurrentUserId } from '@framework/auth';
import { PrivateAIAssistantChatThreadRepository } from '@view-model/application/ai/PrivateAIAssistantChatRepository';
import { useAIAssistantChatHistory } from './useAIAssistantChatHistory';
import { AIAssistantChatDeleteConfirmModal } from './AIAssistantChatDeleteConfirmModal';
import { useStartNewAIAssistantChat } from './useStartNewAIAssistantChat';
import { useAIAssistantRouter } from './aiAssistantRouteAtom';
import {
  getClientSkillForAI,
  getEditableClientSkillForAI,
  listClientSkillsForAI,
  normalizeListClientSkillsInput,
} from './AIAssistantClientSkillTools';
import {
  commitViewModelAIAssistantSkillChange,
  prepareViewModelAIAssistantSkillCreate,
  prepareViewModelAIAssistantSkillUpdate,
  type PreparedViewModelAIAssistantSkillChange,
  type ViewModelAIAssistantSkillChangeErrorCode,
} from '@view-model/application/ai/ViewModelAIAssistantSkillChange';
import { AIAssistantSkillChangeConfirmModal } from './AIAssistantSkillChangeConfirmModal';
import { fetchNodeReactionsJSON, fetchViewModelContentsJSON } from './fetchAIViewModelContents';
import { normalizeInterruptedMessages } from './normalizeRestoredMessage';
import type { RunAIEditToolOutput } from './RunAIEditToolOutput';
import {
  buildSkillCommandCandidates,
  mergeAIAssistantSkillSummaries,
  resolveChatInput,
  type SkillCommandCandidate,
} from './AIAssistantSkillCommands';
import { useBundledAIAssistantSkills } from './useBundledAIAssistantSkills';
import {
  AI_ASSISTANT_SKILL_COMMAND_LISTBOX_ID,
  AIAssistantSkillCommandMenu,
  getSkillCommandOptionId,
} from './AIAssistantSkillCommandMenu';
import { useChatImageAttachments } from './useChatImageAttachments';
import { MAX_CHAT_IMAGE_ATTACHMENTS, uploadChatImageAttachment } from './chatImageAttachment';
import { looksLikeChatImageFile } from './compressChatImage';
import { ChatImageAttachmentPreview } from './ChatImageAttachmentPreview';
import { extractPastedImageFiles } from './pastedChatImage';
import { useChatFileAttachments } from './useChatFileAttachments';
import { GetViewImageToolError, runAIGetViewImageTool } from './runAIGetViewImageTool';
import { sanitizeAIAssistantMessagesForRequest } from './sanitizeAIAssistantMessages';
import { useAIUsageStatus } from '@group/hooks/useAIUsageStatus';
import { useCurrentUserIsLeviiStaff } from '@user/UserEntity/useCurrentUserIsLeviiStaff';
import { AIUsageLimitReachedNotice } from '@user/AIUsageModal/AIUsageLimitReachedNotice';
import {
  MAX_CHAT_FILE_BYTES,
  MAX_CHAT_FILE_ATTACHMENTS,
  MAX_TOTAL_CHAT_ATTACHMENTS,
  uploadChatFileAttachment,
} from './chatFileAttachment';
import { ChatFileChip } from './ChatFileChip';
import { validateAttachmentDrop } from './validateAttachmentDrop';
import { ExportChatFileError, generateExportChatFile } from './exportChatFile';
import { storeExportedChatFile } from './exportedChatFileStore';

export const viewModelContextJSONSchema = toJsonSchema(AIViewModelContextSchema, { errorMode: 'throw' });

type State = {
  sessionId: number;
  input: string;
  // 論理モデルID（モデル名＋パラメータ）。利用可能な一覧は balus-common の AI_MODELS が単一の真実の源。
  model: string;
  mode: AIAssistantMode;
};

// モデル選択ボックスの選択肢。既定は defaultSelectable のモデルのみで、デバッグフラグ有効時に全モデルを表示する。
const buildModelSelectOptions = (includeAllModels: boolean): CustomSelectOption<string>[] =>
  getSelectableAIModels(includeAllModels).map((model) => ({
    value: model.id,
    label: model.label,
  }));

const SKILL_CHANGE_ERROR_MESSAGE_KEYS = {
  invalidSkill: 'viewModel.aiChat.skillChangeValidationError',
  skillLimit: 'viewModel.aiSettings.skillLimitError',
  skillNotFound: 'viewModel.aiChat.skillChangeNotFoundError',
  duplicateName: 'viewModel.aiChat.skillChangeDuplicateNameError',
  conflict: 'viewModel.aiChat.skillChangeConflictError',
} as const satisfies Record<ViewModelAIAssistantSkillChangeErrorCode, ParseKeys>;

/**
 * 編集ツールの成功時にAIへ書き足す補足文言をまとめる。
 * リンク色の自動置き換え、適用ゲートが自動修復した内容、適用ゲートの注意（レイアウトの粗さなど）が
 * それぞれ出ることがある。
 */
function buildEditToolNote(
  replacements: AILinkThemeColorReplacement[],
  evaluation: ApplyEvaluation,
  skippedViewsNote?: string
): string | undefined {
  const { repairText, advisoryText } = formatApplyEvaluation(evaluation);
  return (
    [skippedViewsNote ?? '', formatAILinkThemeColorReplacements(replacements), repairText, advisoryText]
      .filter((text) => text.length > 0)
      .join('\n') || undefined
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

// 消えたビューをスキップしたことをAIに伝える。黙って落とすと、AIは編集済みのつもりで次へ進む。
function buildSkippedViewsNote(skippedViewIds: string[], appliedCount: number): string | undefined {
  if (skippedViewIds.length === 0) return undefined;
  const ids = skippedViewIds.map((id) => `"${id}"`).join(', ');
  const applied = appliedCount > 0 ? 'この編集から除外して残りのビューだけ適用しました' : '編集は適用されていません';
  return `ビューID ${ids} は見つかりませんでした。${applied}。編集中にユーザーが削除した可能性があります。同じ内容で呼び出し直さず、getViews でビュー一覧を取得し直してください。`;
}

/** addView の適用直後に、作業中エフェクトを見せておく最低時間。実際の表示は帯が周回し終える区切りまで伸びる。 */
const JUST_APPLIED_VISIBLE_MS = 1500;

// AIEditToolFailureGuard の打ち切り挙動は、AIが不正なJSONを生成しないと再現できない。
// ?debug=force-edit-tool-error で編集ツールを必ず入力エラーにし、実機で確認できるようにする。
function forcedEditToolInputError(): RunAIEditToolOutput | null {
  if (!isDebugMode('force-edit-tool-error')) return null;
  return { type: 'error', message: 'デバッグモードによる強制的な入力形式エラー', cause: 'invalidToolInput' };
}

interface ChatParamsSource {
  runAIEditTool: (input: unknown) => Promise<RunAIEditToolOutput>;
  runAIPatchViewTool: (input: unknown) => Promise<RunAIEditToolOutput>;
  runAICreateClientSkillTool: (input: unknown) => Promise<RunAIEditToolOutput>;
  runAIUpdateClientSkillTool: (input: unknown) => Promise<RunAIEditToolOutput>;
  state: State;
  selectedViewIds: string[];
}

export function AIAssistantChat({
  viewModelId,
  groupId,
  requestedThreadId,
}: {
  viewModelId: ViewModelId;
  groupId: GroupId;
  requestedThreadId?: string;
}) {
  const { t } = useTranslation();
  const userId = useRequiredCurrentUserId();
  const router = useAIAssistantRouter(viewModelId);
  const startNewChat = useStartNewAIAssistantChat(viewModelId);
  const { skills: bundledSkills, ready: bundledSkillsReady } = useBundledAIAssistantSkills(groupId);
  // `?debug=all-ai-models` 指定時のみ全モデルを選択肢に出す（既定は defaultSelectable のモデルのみ）
  const allModelsEnabled = isDebugMode('all-ai-models');
  const modelSelectOptions = useMemo(() => buildModelSelectOptions(allModelsEnabled), [allModelsEnabled]);
  const [state, setState] = useState<State>(() => {
    const stored = AIAssistantSettingsStorage.getSelectedModel();
    // 保存済みモデルが現在の選択肢にない場合（デバッグモードOFFで重量モデルが保存されている等）はデフォルトに戻す
    const selectableIds = new Set(getSelectableAIModels(allModelsEnabled).map((model) => model.id));
    const initialModel = isAIModelId(stored) && selectableIds.has(stored) ? stored : DEFAULT_AI_MODEL_ID;
    const storedMode = AIAssistantSettingsStorage.getSelectedMode();
    const initialMode = isAIAssistantMode(storedMode) ? storedMode : DEFAULT_AI_ASSISTANT_MODE;
    return {
      sessionId: 0,
      input: '',
      model: initialModel,
      mode: initialMode,
    };
  });
  // リクエストボディ用の現在モード。switchMode ツールの自動再送が再レンダリング前に走っても
  // 最新モードを送れるよう、モード変更時に同期的に更新する ref。
  const modeRef = useRef<AIAssistantMode>(state.mode);
  // リクエストボディ用の現在モデル。transport の body クロージャは useChat が初回のものを
  // 保持し続けるため、最新のモデルを ref 経由で参照する
  const modelRef = useRef<string>(state.model);
  const clientSkillsRef = useRef<AIAssistantClientSkill[]>([]);
  const allClientSkillsRef = useRef<AIAssistantClientSkill[]>([]);
  const [clientSkills, setClientSkills] = useState<AIAssistantClientSkill[]>([]);
  const clientSkillsReadyRef = useRef<Promise<void>>(Promise.resolve());
  const skillChangeConfirmationResolverRef = useRef<((confirmed: boolean) => void) | null>(null);
  const [pendingSkillChangeConfirmation, setPendingSkillChangeConfirmation] =
    useState<PreparedViewModelAIAssistantSkillChange | null>(null);

  const closeSkillChangeConfirmation = (confirmed: boolean): void => {
    skillChangeConfirmationResolverRef.current?.(confirmed);
    skillChangeConfirmationResolverRef.current = null;
    setPendingSkillChangeConfirmation(null);
  };

  const requestSkillChangeConfirmation = (prepared: PreparedViewModelAIAssistantSkillChange): Promise<boolean> => {
    skillChangeConfirmationResolverRef.current?.(false);
    return new Promise((resolve) => {
      skillChangeConfirmationResolverRef.current = resolve;
      setPendingSkillChangeConfirmation(prepared);
    });
  };

  useEffect(() => {
    return () => {
      skillChangeConfirmationResolverRef.current?.(false);
      skillChangeConfirmationResolverRef.current = null;
    };
  }, []);

  useEffect(() => {
    clientSkillsRef.current = [];
    allClientSkillsRef.current = [];
    setClientSkills([]);
    let disposed = false;
    let hasSubscriptionValue = false;
    let readyResolved = false;
    let resolveReady: () => void = () => {};
    const skillsReady = new Promise<void>((resolve) => {
      resolveReady = () => {
        if (readyResolved) return;
        readyResolved = true;
        resolve();
      };
    });
    clientSkillsReadyRef.current = skillsReady;

    void getViewModelAIAssistantSkills(viewModelId)
      .then((skills) => {
        if (!disposed && !hasSubscriptionValue) {
          allClientSkillsRef.current = skills;
          clientSkillsRef.current = skills.filter((skill) => skill.enabled);
          setClientSkills(skills);
        }
      })
      .catch((error) => {
        console.error('Failed to load AI assistant skills', error);
      })
      .finally(resolveReady);

    const unsubscribe = subscribeViewModelAIAssistantSkills(
      viewModelId,
      (skills) => {
        hasSubscriptionValue = true;
        allClientSkillsRef.current = skills;
        clientSkillsRef.current = skills.filter((skill) => skill.enabled);
        setClientSkills(skills);
        resolveReady();
      },
      (error) => {
        console.error('Failed to load AI assistant skills', error);
      }
    );

    return () => {
      disposed = true;
      resolveReady();
      unsubscribe();
    };
  }, [viewModelId]);

  const [viewEntities] = useSnapshot({
    path: RTDBPath.View.viewsPath(viewModelId),
    load: ({ getChildValues }) => {
      return Object.values(getChildValues() as Record<string, ViewJSON>).map((json) => ViewEntity.load(json));
    },
  });

  const { viewModelPermissions, viewModelPageOperationRef, selectedViewIds, focusRect } =
    useAIAssistantOperationContext();
  // useMCPBridgeChat は Set<ViewId> を ref 経由で受け取る契約のため、
  // レンダーごとに最新の Set を ref に載せ替える。
  const selectedViewIdsRef = useRef<Set<ViewId>>(selectedViewIds);
  selectedViewIdsRef.current = selectedViewIds;
  // getViewImage が対象未指定で呼ばれたときの既定対象。AIが編集直後に「どう見えているか」を
  // 確認する流れでは対象を書かないことが多いため、直近に編集したビューを覚えておく。
  const editedViewIdsRef = useRef<string[]>([]);
  // ビューのドラッグは位置更新のたびにこの atom を書き換える。購読するとチャット全体が
  // 毎フレーム再レンダーされてしまうため、ツール実行時に読むだけにする。
  const store = useStore();
  const getViewContents = useCallback(
    () => store.get(viewModelPageContentAtom).viewContentsRecord[viewModelId],
    [store, viewModelId]
  );

  const selectedViewLabel = useMemo(() => {
    if (selectedViewIds.size === 0 || !viewEntities) return null;
    return viewEntities
      .filter((view) => selectedViewIds.has(view.id))
      .map((view) => view.name.value || t('viewModel.aiChat.noTitle'))
      .join(', ');
  }, [selectedViewIds, viewEntities, t]);

  const messageDebugEnabled = AIAssistantSettingsStorage.getDebugMessageEnabled();

  const triggerAIModelingEffect = useHandler((views: readonly AIModelingEffectSource[]) => {
    runAIModelingAppearEffect(collectAIModelingEffectElementIds(views));
  });

  // addView は新規ビューなので、AIが書いている最中は光らせる対象がまだ存在しない。
  // ビューが出来上がった適用直後に、作業中エフェクトを一定時間だけ見せる。
  const [justAppliedViewIds, setJustAppliedViewIds] = useState<string[]>([]);
  const justAppliedTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const markViewsAsJustApplied = useHandler((viewIds: string[]) => {
    if (viewIds.length === 0) return;

    clearTimeout(justAppliedTimerRef.current);
    setJustAppliedViewIds(viewIds);
    justAppliedTimerRef.current = setTimeout(() => setJustAppliedViewIds([]), JUST_APPLIED_VISIBLE_MS);
  });
  useEffect(() => () => clearTimeout(justAppliedTimerRef.current), []);

  // 適用ゲートで使う品質チェックの前提。付箋の期待サイズは ModelingFormat (CJK/LATIN) によって異なる。
  // generateId は修復でIDを振り直すときに使う。generateIds ツールと同じ形式でなければ
  // ViewModelPageOperation のID検証で弾かれるため、同じ nanoid を渡す。
  const { modelingFormat } = useAtomValue(viewModelModelingFormatAtom);
  const qualityCheckOptions = useMemo(
    () => ({
      nodeSize: StickyNodeSizeByModelingFormat[modelingFormat] ?? StickyNodeSizeByModelingFormat['CJK'],
      generateId: () => nanoid(),
    }),
    [modelingFormat]
  );

  // 適用ゲートで既存の要素・ビューと突き合わせるため、現在のビューモデルの状態を取得する。
  // 取得できなかった場合は、渡された編集内容だけで判定できる範囲にとどめる（ゲート自体は止めない）。
  const fetchExistingViewsForApplyGate = async (): Promise<AIViewModelContextView[]> => {
    try {
      return createViewModelContext(await fetchViewModelContentsJSON(viewModelId)).views;
    } catch (error) {
      console.error('適用ゲート用のビューモデル取得に失敗しました', error);
      return [];
    }
  };

  const runAIEditTool = async (input: unknown): Promise<RunAIEditToolOutput> => {
    const forcedError = forcedEditToolInputError();
    if (forcedError) return forcedError;
    const { input: normalizedInput, replacements } = normalizeAIEditLinkThemeColors(omitNullFields(input));
    const result = v.safeParse(AIAssistantClientToolDefs.addView.inputSchema, normalizedInput);

    if (!result.success) {
      console.error('生成した内容がバリデーションで失敗しました', input);
      return { type: 'error', message: formatToolInputValidationError(result.issues), cause: 'invalidToolInput' };
    }

    const viewModelEditParams = result.output;

    if (!viewModelPermissions.isContentEditable) {
      toast.error(t('viewModel.aiChat.toastNotEditable'));
      return {
        type: 'error',
        message: 'ビューモデルの内容を編集できませんでした',
      };
    }

    if (!getViewContents() || !viewEntities) {
      toast.error(t('viewModel.aiChat.toastViewLoading'));
      return {
        type: 'error',
        message: 'ビューモデルの内容を編集できませんでした',
      };
    }

    if (!viewModelPageOperationRef.current) {
      console.error('viewModelPageOperationRef.current is undefined. This is bug.');
      toast.error(t('viewModel.aiChat.toastNotEditable'));
      return {
        type: 'error',
        message: 'ビューモデルの内容を編集できませんでした',
      };
    }

    // patchView ではビューを動かせず、作ってから注意を返してもAIは直せないため、作成自体を見送る。
    const outsideCanvasMessage = describeViewsOutsideCanvas(viewModelEditParams.views);
    if (outsideCanvasMessage) return { type: 'noop', message: outsideCanvasMessage };

    // 適用ゲート: 編集内容の品質チェックはここに集約している。
    // データが壊れる問題（ID重複・存在しない要素へのリンク）は自動修復してから適用し、
    // 修復内容とレイアウトの粗さを注意としてAIに伝える。
    // addView はAIが指定した position にビューが置かれるので、既存ビューとの重なりも検査する。
    const evaluation = evaluateEditForApply(viewModelEditParams.views, {
      ...qualityCheckOptions,
      existingViews: await fetchExistingViewsForApplyGate(),
    });

    const addedViewsRect = await viewModelPageOperationRef.current.runAIViewModelEditInstruction(
      { ...viewModelEditParams, views: evaluation.views },
      viewEntities
    );
    editedViewIdsRef.current = evaluation.views.map((view) => view.id);

    // 追加したビューはAIが指定したキャンバス座標に置かれ画面外のこともあるため、そこへカメラを移動する
    if (addedViewsRect) focusRect(addedViewsRect);

    triggerAIModelingEffect(evaluation.views);
    markViewsAsJustApplied(evaluation.views.map((view) => view.id));
    toast.success(t('viewModel.aiChat.toastEditSuccess'));
    return { type: 'ok', note: buildEditToolNote(replacements, evaluation) };
  };

  const runAIPatchViewTool = async (input: unknown): Promise<RunAIEditToolOutput> => {
    const forcedError = forcedEditToolInputError();
    if (forcedError) return forcedError;
    const { input: normalizedInput, replacements } = normalizeAIEditLinkThemeColors(omitNullFields(input));
    const result = v.safeParse(AIAssistantClientToolDefs.patchView.inputSchema, normalizedInput);

    if (!result.success) {
      console.error('生成した内容がバリデーションで失敗しました', input);
      return { type: 'error', message: formatToolInputValidationError(result.issues), cause: 'invalidToolInput' };
    }

    const patchViewParams = result.output;

    if (!viewModelPermissions.isContentEditable) {
      toast.error(t('viewModel.aiChat.toastNotEditable'));
      return {
        type: 'error',
        message: 'ビューモデルの内容を編集できませんでした',
      };
    }

    if (!viewEntities) {
      toast.error(t('viewModel.aiChat.toastViewLoading'));
      return {
        type: 'error',
        message: 'ビューモデルの内容を編集できませんでした',
      };
    }

    if (!viewModelPageOperationRef.current) {
      console.error('viewModelPageOperationRef.current is undefined. This is bug.');
      toast.error(t('viewModel.aiChat.toastNotEditable'));
      return {
        type: 'error',
        message: 'ビューモデルの内容を編集できませんでした',
      };
    }

    // 適用ゲート: 差分だけでは既存要素との関係を判定できないので、現在のビューの状態を取得して渡す。
    // 「既存要素との重なり」「既存要素へのリンクの妥当性」「既存の別種要素とのID衝突」はこれがないと判定できない。
    const existingViewsById: Record<string, AIViewModelContextView | undefined> = Object.fromEntries(
      (await fetchExistingViewsForApplyGate()).map((view) => [view.id, view])
    );

    const evaluation = evaluatePatchForApply(patchViewParams.views, existingViewsById, qualityCheckOptions);

    const { skippedViewIds } = await viewModelPageOperationRef.current.runAIViewModelPatchView(
      { ...patchViewParams, views: evaluation.views },
      viewEntities
    );
    const appliedViews = evaluation.views.filter((view) => !skippedViewIds.includes(view.id));
    const skippedViewsNote = buildSkippedViewsNote(skippedViewIds, appliedViews.length);

    // 対象がすべて消えていた場合はエラーではなく空振りとして返す。エラーにするとAIが同じ入力を送り直す。
    if (appliedViews.length === 0) {
      return { type: 'noop', message: skippedViewsNote ?? '編集対象のビューが見つかりませんでした。' };
    }

    editedViewIdsRef.current = appliedViews.map((view) => view.id);

    triggerAIModelingEffect(appliedViews);
    toast.success(t('viewModel.aiChat.toastPatchSuccess'));
    const note = buildEditToolNote(replacements, evaluation, skippedViewsNote);
    // 一部でも取りこぼしたら ok にしない。ok は打ち切りカウンタを戻すので、同じ入力の再送を止められなくなる。
    return skippedViewIds.length > 0
      ? { type: 'partial', message: '一部のビューにだけ差分編集を適用しました。', note }
      : { type: 'ok', note };
  };

  const runPreparedAISkillTool = async (
    prepared: PreparedViewModelAIAssistantSkillChange
  ): Promise<RunAIEditToolOutput> => {
    const confirmed = await requestSkillChangeConfirmation(prepared);
    if (!confirmed) {
      return { type: 'ok', message: t('viewModel.aiChat.skillChangeCanceledResult') };
    }

    let result;
    try {
      result = await commitViewModelAIAssistantSkillChange(viewModelId, prepared);
    } catch (error) {
      console.error('Failed to save AI assistant skill', error);
      const message = t('viewModel.aiChat.skillChangeSaveFailed');
      toast.error(message);
      return { type: 'error', message };
    }
    if (result.type === 'error') {
      const message = t(SKILL_CHANGE_ERROR_MESSAGE_KEYS[result.code]);
      toast.error(message);
      return { type: 'error', message };
    }

    allClientSkillsRef.current = result.skills;
    clientSkillsRef.current = result.skills.filter((skill) => skill.enabled);
    const message =
      prepared.operation === 'create'
        ? t('viewModel.aiChat.skillChangeCreateSuccess')
        : t('viewModel.aiChat.skillChangeUpdateSuccess');
    toast.success(message);
    return { type: 'ok', message };
  };

  const runAICreateClientSkillTool = async (input: unknown): Promise<RunAIEditToolOutput> => {
    await clientSkillsReadyRef.current;
    const parsedInput = v.safeParse(AIAssistantClientToolDefs.createClientSkill.inputSchema, input);
    if (!parsedInput.success) {
      return { type: 'error', message: t('viewModel.aiChat.skillChangeValidationError') };
    }
    if (!viewModelPermissions.isSkillEditable) {
      toast.error(t('viewModel.aiChat.toastNotEditable'));
      return { type: 'error', message: t('viewModel.aiChat.toastNotEditable') };
    }

    const preparedResult = prepareViewModelAIAssistantSkillCreate(parsedInput.output.skill, allClientSkillsRef.current);
    if (preparedResult.type === 'error') {
      const message = t(SKILL_CHANGE_ERROR_MESSAGE_KEYS[preparedResult.code]);
      toast.error(message);
      return { type: 'error', message };
    }
    return runPreparedAISkillTool(preparedResult.prepared);
  };

  const runAIUpdateClientSkillTool = async (input: unknown): Promise<RunAIEditToolOutput> => {
    await clientSkillsReadyRef.current;
    const parsedInput = v.safeParse(AIAssistantClientToolDefs.updateClientSkill.inputSchema, input);
    if (!parsedInput.success) {
      return { type: 'error', message: t('viewModel.aiChat.skillChangeValidationError') };
    }
    if (!viewModelPermissions.isSkillEditable) {
      toast.error(t('viewModel.aiChat.toastNotEditable'));
      return { type: 'error', message: t('viewModel.aiChat.toastNotEditable') };
    }

    const expectedSkill = getEditableClientSkillForAI(allClientSkillsRef.current, parsedInput.output.id);
    if (!expectedSkill) {
      const message = t('viewModel.aiChat.skillChangeNotFoundError');
      toast.error(message);
      return { type: 'error', message };
    }

    const preparedResult = prepareViewModelAIAssistantSkillUpdate(
      expectedSkill,
      parsedInput.output.skill,
      allClientSkillsRef.current
    );
    if (preparedResult.type === 'error') {
      const message = t(SKILL_CHANGE_ERROR_MESSAGE_KEYS[preparedResult.code]);
      toast.error(message);
      return { type: 'error', message };
    }
    return runPreparedAISkillTool(preparedResult.prepared);
  };

  // 手動チャットとMCP経路は「一方の状態がもう一方に影響しない」のが仕様なのでカウンタを分ける。
  // useChat の引数クロージャから参照するためインスタンスは固定する。
  const editToolFailureGuardsRef = useRef<{ manual: AIEditToolFailureGuard; mcp: AIEditToolFailureGuard } | null>(null);
  if (editToolFailureGuardsRef.current === null) {
    editToolFailureGuardsRef.current = { manual: new AIEditToolFailureGuard(), mcp: new AIEditToolFailureGuard() };
  }
  const editToolFailureGuard = editToolFailureGuardsRef.current.manual;
  const mcpEditToolFailureGuard = editToolFailureGuardsRef.current.mcp;

  // 停止・unmount 後に完了した client tool の addToolOutput が、SDK の自動再送で次ターンを始めてしまうのを防ぐ
  const isStoppedRef = useRef(false);
  useEffect(() => {
    isStoppedRef.current = false;
    return () => {
      isStoppedRef.current = true;
    };
  }, []);

  // 上限に達したら送信をブロックする。利用額はRTDB購読で自動更新されるので取り直しは不要。
  // レヴィ社内ユーザーはサーバー側の上限判定から外れているため、警告も送信ブロックもしない。
  // この画面はビューモデルを開くたびにマウントされるので、使わない利用状況の取得・購読は skip で止める
  const isLeviiStaff = useCurrentUserIsLeviiStaff();
  const { status: aiUsageStatus } = useAIUsageStatus(groupId, { skip: isLeviiStaff });
  const isOverAIUsageLimit = !isLeviiStaff && aiUsageStatus !== null && aiUsageStatus.usedRatio >= 1;
  const isNearAIUsageLimit =
    !isLeviiStaff && aiUsageStatus !== null && !isOverAIUsageLimit && aiUsageStatus.usedRatio >= 0.8;

  // NOTE: useChat()の引数は最初に渡した関数が内部で保持されるので、最新の情報を使う場合にはrefで挟む必要ある
  const chatParamsSourceRef = useChatParamsSourceRef({
    runAIEditTool,
    runAIPatchViewTool,
    runAICreateClientSkillTool,
    runAIUpdateClientSkillTool,
    state,
    selectedViewIds: [...selectedViewIds],
  });

  const {
    messages,
    sendMessage,
    status,
    setMessages,
    addToolOutput: addToolOutputToChat,
    stop,
  } = useChat<UIMessage>({
    // IDが変わるとチャット履歴がリセットされるようにする
    // （モデル変更ではリセットしないため model は id に含めない）
    id: `aichat-${viewModelId}-${state.sessionId}`,
    throttle: CHAT_STREAMING_THROTTLE_MS,
    // 止めないと output-error でも SDK が次ターンを再送し、AIが禁止指示に従うかに賭ける形になる。
    sendAutomaticallyWhen: ({ messages: currentMessages }) =>
      !isStoppedRef.current &&
      !editToolFailureGuard.isTripped &&
      lastAssistantMessageIsCompleteWithToolCalls({ messages: currentMessages }),
    transport: new DefaultChatTransport({
      api: '/api/aichat',
      headers: {
        'x-application-name': 'balus-webapp',
      },
      // 既定のボディ組み立てと同じ形で、messages だけ差し替える。
      // ここを指定するとボディ全体が置き換わるため id / trigger / messageId も自分で載せる。
      prepareSendMessagesRequest: ({ id, messages: outgoing, body, trigger, messageId }) => ({
        body: {
          ...body,
          id,
          messages: sanitizeAIAssistantMessagesForRequest(outgoing),
          trigger,
          messageId,
        },
      }),
      body: async () => {
        await clientSkillsReadyRef.current;
        const viewModelContents = await fetchViewModelContentsJSON(viewModelId);
        const modelIds = viewModelContents.models.map((model) => extractId(model.key));
        const stickyNodeReactions = await fetchNodeReactionsJSON(viewModelId, modelIds);
        const viewModelContext = createViewModelContext(viewModelContents, {
          stickyNodeReactions,
        });
        const selectedViewIds = chatParamsSourceRef.current.selectedViewIds;

        const payload: Record<string, unknown> = {
          // モデル変更でチャット履歴をリセットしないため id には含めず、最新値を ref 経由で送る
          model: modelRef.current,
          groupId,
          viewModelId,
          // モード切り替えでチャット履歴をリセットしないため id には含めず、最新値を ref 経由で送る
          mode: modeRef.current,
          viewModelContext,
          selectedViewIds,
          clientSkills: clientSkillsRef.current,
        };

        return payload;
      },
    }),

    onError(error) {
      console.error(error);
      toast.error(
        error.message.includes(AI_USAGE_LIMIT_EXCEEDED_MESSAGE)
          ? t('framework.aiUsage.overLimitMessage')
          : t('viewModel.aiChat.toastConnectionError')
      );
      // ストリームが異常終了（タイムアウト・ネットワーク断等）した場合、実行途中のまま
      // 残った tool part を中断扱いに確定させる。放置すると「実行中...」が表示され続け、
      // その状態のまま履歴に保存されてしまう。
      // このクロージャは onFinish 同様、毎レンダリング最新に差し替えられる。
      finalizeInterruptedToolParts();
    },

    onFinish: ({ messages: finalMessages }) => {
      // 履歴復元前(status==='restoring')は saveMessages を持たないので何もしない。
      // この onFinish のクロージャは useChat 内部で毎レンダリング最新に差し替えられるため、
      // ここで参照する history は常に最新の状態を見る。
      if (history.status === 'ready') {
        void history.saveMessages(finalMessages);
      }
    },

    onToolCall: async ({ toolCall }) => {
      if (toolCall.dynamic) {
        return;
      }

      // サーバ側で解決できていないtool呼び出しはクライアントで解決する
      if (!toolCall.providerExecuted) {
        // OpenAI の Structured Outputs は省略可能な項目を nullable で表現するため、モデルは
        // 「値なし」を null で送ってくる。valibot の optional は undefined しか受け付けないので、
        // パースに渡す前に null のフィールドを落として「省略された」状態に揃える。
        const toolInput = omitNullFields(toolCall.input);

        // 打ち切ると自動再送を止めるためAIの発話が増えず、ツール結果の折りたたみ表示しか
        // 残らない。ユーザーには「送信したのに黙って止まった」ように見えるので通知する。
        // 打ち切りへ切り替わった一度だけ出す（同一メッセージ内の後続呼び出しで重複させない）。
        const runEditToolWithGuard = async (run: () => Promise<RunAIEditToolOutput>): Promise<RunAIEditToolOutput> => {
          const trippedBefore = editToolFailureGuard.isTripped;
          const result = await editToolFailureGuard.run(run);
          if (!trippedBefore && editToolFailureGuard.isTripped) {
            toast.error(t('viewModel.aiChat.toastEditToolStopped'));
          }
          return result;
        };

        const handleClientTool = async (
          toolName: string,
          toolCallId: string,
          run: () => Promise<RunAIEditToolOutput>,
          successMessage: string
        ) => {
          let result;
          try {
            result = await run();
          } catch (error) {
            console.error('処理の呼び出しに失敗しました', error);
            addToolOutput({
              state: 'output-error',
              tool: toolName,
              toolCallId,
              errorText: error instanceof Error ? error.message : '何らかの原因で処理の呼び出しに失敗しました',
            });
            return;
          }
          if (result.type === 'error') {
            addToolOutput({
              state: 'output-error',
              tool: toolName,
              toolCallId,
              errorText: result.message,
            });
          } else {
            const output = result.message ?? successMessage;
            addToolOutput({
              state: 'output-available',
              tool: toolName,
              toolCallId,
              output: result.note ? `${output}\n${result.note}` : output,
            });
          }
        };

        if (toolCall.toolName === AIAssistantToolNames.addView) {
          await handleClientTool(
            toolCall.toolName,
            toolCall.toolCallId,
            () => runEditToolWithGuard(() => chatParamsSourceRef.current.runAIEditTool(toolInput)),
            '追加しました。'
          );
          return;
        }

        if (toolCall.toolName === AIAssistantToolNames.patchView) {
          await handleClientTool(
            toolCall.toolName,
            toolCall.toolCallId,
            () => runEditToolWithGuard(() => chatParamsSourceRef.current.runAIPatchViewTool(toolInput)),
            'ビューの内容を差分編集しました。'
          );
          return;
        }

        if (toolCall.toolName === AIAssistantToolNames.createClientSkill) {
          await handleClientTool(
            toolCall.toolName,
            toolCall.toolCallId,
            () => chatParamsSourceRef.current.runAICreateClientSkillTool(toolInput),
            t('viewModel.aiChat.skillChangeCreateSuccess')
          );
          return;
        }

        if (toolCall.toolName === AIAssistantToolNames.updateClientSkill) {
          await handleClientTool(
            toolCall.toolName,
            toolCall.toolCallId,
            () => chatParamsSourceRef.current.runAIUpdateClientSkillTool(toolInput),
            t('viewModel.aiChat.skillChangeUpdateSuccess')
          );
          return;
        }

        if (toolCall.toolName === AIAssistantToolNames.listClientSkills) {
          await clientSkillsReadyRef.current;
          const parsed = v.safeParse(
            AIAssistantClientToolDefs.listClientSkills.inputSchema,
            normalizeListClientSkillsInput(toolInput)
          );
          if (!parsed.success) {
            addToolOutput({
              state: 'output-error',
              tool: toolCall.toolName,
              toolCallId: toolCall.toolCallId,
              errorText: 'スキル一覧取得の入力形式が不正です。',
            });
            return;
          }
          addToolOutput({
            state: 'output-available',
            tool: toolCall.toolName,
            toolCallId: toolCall.toolCallId,
            output: listClientSkillsForAI(allClientSkillsRef.current),
          });
          return;
        }

        if (toolCall.toolName === AIAssistantToolNames.getClientSkill) {
          await clientSkillsReadyRef.current;
          const parsed = v.safeParse(AIAssistantClientToolDefs.getClientSkill.inputSchema, toolInput);
          if (!parsed.success) {
            addToolOutput({
              state: 'output-error',
              tool: toolCall.toolName,
              toolCallId: toolCall.toolCallId,
              errorText: 'スキルIDの形式が不正です。',
            });
            return;
          }
          addToolOutput({
            state: 'output-available',
            tool: toolCall.toolName,
            toolCallId: toolCall.toolCallId,
            output: getClientSkillForAI(allClientSkillsRef.current, parsed.output.id),
          });
          return;
        }

        if (toolCall.toolName === AIAssistantToolNames.switchMode) {
          const parsed = v.safeParse(AIAssistantClientToolDefs.switchMode.inputSchema, toolInput);
          if (!parsed.success) {
            addToolOutput({
              state: 'output-error',
              tool: toolCall.toolName,
              toolCallId: toolCall.toolCallId,
              errorText: 'モードの指定が不正です。chat または modeling を指定してください。',
            });
            return;
          }
          const nextMode = parsed.output.mode;
          const modeLabel =
            nextMode === 'modeling' ? t('viewModel.aiChat.modeModeling') : t('viewModel.aiChat.modeChat');

          if (nextMode === modeRef.current) {
            addToolOutput({
              state: 'output-available',
              tool: toolCall.toolName,
              toolCallId: toolCall.toolCallId,
              output: `すでに「${modeLabel}」モードです。`,
            });
            return;
          }

          // 切り替え前にユーザーへ確認ダイアログを表示し、同意を得てからモードを切り替える
          const confirmed = window.confirm(t('viewModel.aiChat.switchModeConfirm', { mode: modeLabel }));
          if (!confirmed) {
            addToolOutput({
              state: 'output-available',
              tool: toolCall.toolName,
              toolCallId: toolCall.toolCallId,
              output: 'ユーザーがモードの切り替えをキャンセルしました。現在のモードのまま続けてください。',
            });
            return;
          }

          // 直後に走る自動再送が新モードで送られるよう、state 更新前に ref を同期する
          modeRef.current = nextMode;
          AIAssistantSettingsStorage.setSelectedMode(nextMode);
          setState((prev) => ({ ...prev, mode: nextMode }));
          addToolOutput({
            state: 'output-available',
            tool: toolCall.toolName,
            toolCallId: toolCall.toolCallId,
            output: `モードを「${modeLabel}」に切り替えました。`,
          });
          return;
        }

        if (toolCall.toolName === AIAssistantToolNames.getViewImage) {
          try {
            const output = await runAIGetViewImageTool(toolInput, {
              editedViewIds: editedViewIdsRef.current,
              selectedViewIds: [...selectedViewIdsRef.current],
            });
            addToolOutput({
              state: 'output-available',
              tool: toolCall.toolName,
              toolCallId: toolCall.toolCallId,
              output,
            });
          } catch (error) {
            addToolOutput({
              state: 'output-error',
              tool: toolCall.toolName,
              toolCallId: toolCall.toolCallId,
              errorText:
                error instanceof GetViewImageToolError
                  ? error.message
                  : 'ビュー画像の取得に失敗しました。少し待ってから再度お試しください。',
            });
          }
          return;
        }

        if (toolCall.toolName === AIAssistantToolNames.exportFile) {
          const parsed = v.safeParse(AIAssistantClientToolDefs.exportFile.inputSchema, toolInput);
          if (!parsed.success) {
            const detail = parsed.issues.map((issue) => issue.message).find(Boolean);
            const format = isRecord(toolInput) ? toolInput.format : undefined;
            const isTextFormat = format === 'md' || format === 'txt';
            const limitReminder = isTextFormat
              ? `制限: filenameは100文字以内でパス区切り不可、contentは${AI_EXPORT_TEXT_MAX_CHARS.toLocaleString()}文字まで。`
              : '制限: filenameは100文字以内でパス区切り不可、csv・tsvは1シートのみ、セル合計5000個・1セル500文字まで。';
            addToolOutput({
              state: 'output-error',
              tool: toolCall.toolName,
              toolCallId: toolCall.toolCallId,
              errorText: `ファイル生成の入力が不正です。${detail ?? ''} ${limitReminder}`,
            });
            return;
          }
          try {
            const { blob, downloadFilename } = await generateExportChatFile(parsed.output);
            storeExportedChatFile(toolCall.toolCallId, { blob, filename: downloadFilename });
            addToolOutput({
              state: 'output-available',
              tool: toolCall.toolName,
              toolCallId: toolCall.toolCallId,
              output:
                `ファイル「${downloadFilename}」を生成しました。チャット上のカードのボタンからダウンロードできます。` +
                '転記した内容が元データと一致しているか、ユーザーに確認を促してください。',
            });
          } catch (error) {
            addToolOutput({
              state: 'output-error',
              tool: toolCall.toolName,
              toolCallId: toolCall.toolCallId,
              errorText:
                error instanceof ExportChatFileError
                  ? error.message
                  : 'ファイルの生成に失敗しました。入力内容を見直して再度お試しください。',
            });
          }
          return;
        }
      }
    },
  });

  const addInitialPrompt = async () => {
    const params = new URLSearchParams(location.search);

    // フラグで分岐させる
    const shouldEmbed = params.has('embed-view-model');

    const customSystemPrompt = AIAssistantSettingsStorage.getCustomSystemPrompt();
    const embeddedViewModelPrompt = !shouldEmbed
      ? ''
      : await (async () => {
          const viewModelContentJSON = await fetchViewModelContentsJSON(viewModelId);
          const viewModelContext = createViewModelContext(viewModelContentJSON);
          return `この会話はこのビューモデルを踏まえて返答してください:

\`\`\`
${JSON.stringify(viewModelContext)}
\`\`\`

このJSONのスキーマは以下です:

\`\`\`
${JSON.stringify(viewModelContextJSONSchema)}
\`\`\`
`;
        })();

    const systemMessage = buildAISystemMessage([customSystemPrompt, embeddedViewModelPrompt]);
    if (systemMessage) {
      setMessages([systemMessage]);
    }
  };

  const history = useAIAssistantChatHistory({
    viewModelId,
    userId,
    setMessages,
    requestedThreadId,
  });

  // 実行途中のまま残った tool part を中断扱い（output-error）に確定させ、履歴にも保存する。
  // setMessages の updater 形式で最新のメッセージを参照する（このコンポーネントの
  // messages 変数はレンダー時点のスナップショットのため、直前に届いた chunk を取りこぼしうる）。
  // 保存は updater の同期実行に依存させず、フラグを立てて state 反映後の effect で行う。
  const pendingInterruptedSaveRef = useRef(false);
  const finalizeInterruptedToolParts = () => {
    pendingInterruptedSaveRef.current = true;
    setMessages((current) => normalizeInterruptedMessages(current, t('viewModel.aiChat.toolInterrupted')) ?? current);
  };

  // 停止後は自動再送されず onFinish も走らないため、遅れて届いた tool の結果はここで履歴に保存する
  const addToolOutput: typeof addToolOutputToChat = async (output) => {
    await addToolOutputToChat(output);
    if (isStoppedRef.current) {
      pendingInterruptedSaveRef.current = true;
      // 同じ配列を渡すと再レンダーされず、保存の effect が走らない
      setMessages((current) => [...current]);
    }
  };

  // pendingInterruptedSaveRef を立てた state 更新の反映後に、正規化済みメッセージを履歴へ保存する。
  // updater の反映がこのレンダーに間に合っていない場合に備えて、保存前にもう一度正規化をかける
  // （反映済みなら no-op で messages がそのまま使われる）。
  useEffect(() => {
    if (!pendingInterruptedSaveRef.current) return;
    pendingInterruptedSaveRef.current = false;
    if (history.status !== 'ready') return;
    const normalized = normalizeInterruptedMessages(messages, t('viewModel.aiChat.toolInterrupted'));
    void history.saveMessages(normalized ?? messages);
  });

  const {
    isOpen: isMenuOpen,
    popupRef: menuPopupRef,
    handleOpen: openMenu,
    handleClose: closeMenu,
  } = usePopupRef<HTMLDivElement>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const handleDeleteChat = async () => {
    if (history.status !== 'ready') return;
    try {
      await PrivateAIAssistantChatThreadRepository.deleteThread(viewModelId, userId, history.threadId);
      setIsDeleteModalOpen(false);
      // 削除したスレッドは開けなくなるため一覧へ戻す
      router.replace({ page: 'list' });
      toast.success(t('viewModel.aiChat.toastDeleteSuccess'));
    } catch (e) {
      console.error('Failed to delete AI assistant chat thread', e);
      toast.error(t('viewModel.aiChat.toastDeleteError'));
    }
  };

  // 画像・ファイル両フックが互いの枚数を参照して合計上限（4個）を算出するための共有カウント。
  // ゲッター（関数）で渡すことで、フック同士の相互参照（TDZ）を避けつつ最新値を読む。
  const attachmentCountsRef = useRef({ images: 0, files: 0 });
  const getImageCount = useCallback(() => attachmentCountsRef.current.images, []);
  const getFileCount = useCallback(() => attachmentCountsRef.current.files, []);
  const {
    attachments,
    addFiles: addImageAttachments,
    removeAttachment: removeImageAttachment,
    clear: clearImageAttachments,
    isProcessing: isProcessingAttachments,
  } = useChatImageAttachments(getFileCount);
  const {
    attachments: fileAttachments,
    addFiles: addFileAttachments,
    removeAttachment: removeFileAttachment,
    clear: clearFileAttachments,
  } = useChatFileAttachments(getImageCount);
  attachmentCountsRef.current = { images: attachments.length, files: fileAttachments.length };
  const totalAttachmentCount = attachments.length + fileAttachments.length;
  // 送信（アップロード〜dispatch）中の二重送信を防ぐフラグ
  const [isSending, setIsSending] = useState(false);

  const isStreaming = ['submitted', 'streaming'].includes(status);
  const skillCommandCandidates = useMemo(
    () => buildSkillCommandCandidates(mergeAIAssistantSkillSummaries(bundledSkills, clientSkills)),
    [bundledSkills, clientSkills]
  );
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const currentInputRef = useRef(state.input);
  currentInputRef.current = state.input;
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [dismissedSkillCommandInput, setDismissedSkillCommandInput] = useState<string | null>(null);
  const [forcedSkillCommandCandidates, setForcedSkillCommandCandidates] = useState<
    readonly SkillCommandCandidate[] | null
  >(null);
  const [highlightedSkillCommandIndex, setHighlightedSkillCommandIndex] = useState(0);

  const prefixSkillCommandCandidates = useMemo(() => {
    if (!/^\/[^\s]*$/.test(state.input)) return [];
    return skillCommandCandidates.filter((candidate) => candidate.trigger.startsWith(state.input));
  }, [skillCommandCandidates, state.input]);
  const visibleSkillCommandCandidates = forcedSkillCommandCandidates ?? prefixSkillCommandCandidates;
  const visibleSkillCommandCandidateCount = visibleSkillCommandCandidates.length;
  const isSkillCommandMenuOpen =
    isInputFocused &&
    visibleSkillCommandCandidateCount > 0 &&
    (forcedSkillCommandCandidates !== null || dismissedSkillCommandInput !== state.input);

  useEffect(() => {
    setHighlightedSkillCommandIndex(0);
  }, [state.input, forcedSkillCommandCandidates, visibleSkillCommandCandidateCount]);

  const selectSkillCommand = (candidate: SkillCommandCandidate): void => {
    const firstWhitespaceIndex = state.input.search(/\s/);
    const nextInput =
      firstWhitespaceIndex === -1
        ? `${candidate.trigger} `
        : candidate.trigger + state.input.slice(firstWhitespaceIndex);
    currentInputRef.current = nextInput;
    setState((prev) => ({ ...prev, input: nextInput }));
    setForcedSkillCommandCandidates(null);
    setDismissedSkillCommandInput(nextInput);
    textareaRef.current?.focus();
  };
  // テキストか添付（画像・ファイル）のどちらかがあれば送信可能（添付のみ送信を許可）
  const canSend =
    ['ready', 'error'].includes(status) &&
    !isSending &&
    !isProcessingAttachments &&
    !isOverAIUsageLimit &&
    (state.input.trim().length > 0 || totalAttachmentCount > 0);

  const sessionRef = useRef<MCPBridgeChatSession | null>(null);
  if (sessionRef.current === null) {
    sessionRef.current = new MCPBridgeChatSession();
  }
  const session = sessionRef.current;

  // MCP 経由の対話は手動チャットと別の useChat インスタンスで走らせる。
  // これにより MCP のメッセージは RTDB 永続化・チャット画面描画・スレッド一覧から遮断される。
  const {
    sendMessageWithFreshHistory: mcpSendMessageWithFreshHistory,
    status: mcpStatus,
    messages: mcpMessages,
  } = useMCPBridgeChat({
    viewModelId,
    groupId,
    runAIEditTool,
    runAIPatchViewTool,
    selectedViewIdsRef,
    modelRef,
    session,
    editToolFailureGuard: mcpEditToolFailureGuard,
  });

  const mcpStatusRef = useRef<typeof mcpStatus>(mcpStatus);
  mcpStatusRef.current = mcpStatus;

  // AIが内容を書いている対象ビューをキャンバス側へ渡す。手動チャットとMCP経由は別インスタンスなので両方見る。
  const setAIWorkingViewIds = useSetAtom(aiWorkingViewIdsAtom);
  useEffect(() => {
    // 応答が中断されると tool part が入力生成中のまま残ることがあるので、status でも打ち切る
    const viewIds = [
      ...new Set([
        ...(isChatInflight(status) ? extractAIWorkingViewIds(messages) : []),
        ...(isChatInflight(mcpStatus) ? extractAIWorkingViewIds(mcpMessages) : []),
        ...justAppliedViewIds,
      ]),
    ];
    // messages はストリーミング中に毎回作り直されるため、中身が同じなら参照を保って再描画を避ける
    setAIWorkingViewIds((prev) =>
      prev.length === viewIds.length && prev.every((id, index) => id === viewIds[index]) ? prev : viewIds
    );
  }, [messages, mcpMessages, status, mcpStatus, justAppliedViewIds, setAIWorkingViewIds]);

  useEffect(() => () => setAIWorkingViewIds([]), [setAIWorkingViewIds]);

  const mcpBridgeRunner = useHandler<BridgeChatRunner>(async (instruction) => {
    // mcpStatusRef.current で MCP 用 useChat の最新 status を参照する。
    // useHandler のクロージャがキャプチャするレンダー時点の status は
    // 手動送信直後に再レンダーが走る前は古い値を持つ場合があるため。
    return session.start(instruction, mcpStatusRef.current, (text) => mcpSendMessageWithFreshHistory({ text }));
  });

  // isTripped はミュータブルなインスタンスのゲッタなので、値が変わってもそれ自体は再レンダーを
  // 起こさない。この effect が打ち切りを観測できるのは、guard の状態を変える run() の後に
  // addToolOutput が走って mcpMessages が更新されるため。呼び出し順が入れ替わるとMCPセッションが
  // タイムアウトまで pending のままになるので、useMCPBridgeChat 側の順序を変えるときは要注意。
  useEffect(() => {
    if (!session.isPending) return;
    session.handleStatusChange({
      status: mcpStatus,
      isAIResponseComplete: isAIResponseComplete(mcpMessages, mcpEditToolFailureGuard.isTripped),
      editToolFailureStopped: mcpEditToolFailureGuard.isTripped,
    });
  }, [mcpStatus, mcpMessages, session, mcpEditToolFailureGuard.isTripped]);

  useEffect(() => {
    MCPBridgeChatLocator.register(mcpBridgeRunner);
    return () => {
      MCPBridgeChatLocator.unregister(mcpBridgeRunner);
      session.abort('AIAssistantChat is no longer available');
    };
  }, [mcpBridgeRunner, session]);

  const { scrollContainerRef, scrollContentRef, onScroll, stickToBottom } = useStickToBottomScroll();

  // 入力フッターへの画像 D&D。noClick でドロップ領域全体のクリック誤爆を防ぎ、クリップボタンから open() する。
  const {
    getRootProps: getDropzoneRootProps,
    getInputProps: getDropzoneInputProps,
    open: openFilePicker,
    isDragActive,
  } = useDropzone({
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length === 0) return;
      // ドロップされたファイルを種別（画像 / ファイル）ごとに振り分ける。画像は MIME タイプで判定し、
      // それ以外は全てファイル候補として扱う（スプレッドシート/テキストいずれも拡張子では絞り込まない。
      // 実際に読み込めるかはバックエンドが内容そのもので判定し、読み込めなければ「読み込めません」と表示される）。
      const imageFiles = acceptedFiles.filter((file) => looksLikeChatImageFile(file));
      const attachFiles = acceptedFiles.filter((file) => !looksLikeChatImageFile(file));

      // 1回のドロップ全体を all-or-nothing で検証し、上限に触れたら種別をまたいで全件中止する。
      const dropError = validateAttachmentDrop({
        currentImageCount: attachments.length,
        currentFileCount: fileAttachments.length,
        newImageCount: imageFiles.length,
        newFileSizes: attachFiles.map((file) => file.size),
        limits: {
          imageMax: MAX_CHAT_IMAGE_ATTACHMENTS,
          fileMax: MAX_CHAT_FILE_ATTACHMENTS,
          totalMax: MAX_TOTAL_CHAT_ATTACHMENTS,
          fileByteLimit: MAX_CHAT_FILE_BYTES,
        },
      });
      if (dropError) {
        toast.error(t(dropError.key, dropError.params));
        return;
      }

      if (imageFiles.length > 0) void addImageAttachments(imageFiles);
      if (attachFiles.length > 0) addFileAttachments(attachFiles);
    },
    // accept は敢えて指定しない。画像/ファイルの振り分けは onDrop 内の MIME 判定で行い、
    // ファイル形式の可否（読み込めるかどうか）はバックエンドが内容そのもので判定する。
    noClick: true,
    noKeyboard: true,
  });

  const sendCurrentInput = async () => {
    if (history.status !== 'ready' || isSending) return;
    const threadId = history.threadId;
    const lastInput = state.input;
    const pendingImages = attachments;
    const pendingFiles = fileAttachments;
    if (lastInput.trim().length === 0 && pendingImages.length === 0 && pendingFiles.length === 0) return;

    setIsSending(true);
    try {
      let chatInputResolution = resolveChatInput(lastInput, []);
      if (lastInput.startsWith('/')) {
        await clientSkillsReadyRef.current;
        const bundledSkills = await bundledSkillsReady;
        const skillSummaries = mergeAIAssistantSkillSummaries(bundledSkills, allClientSkillsRef.current);
        chatInputResolution = resolveChatInput(lastInput, buildSkillCommandCandidates(skillSummaries));
      }
      if (chatInputResolution.type === 'invalidSkillCommand') {
        if (currentInputRef.current !== lastInput) return;
        if (chatInputResolution.reason === 'ambiguous') {
          setForcedSkillCommandCandidates(chatInputResolution.candidates);
          setDismissedSkillCommandInput(null);
          setHighlightedSkillCommandIndex(0);
          textareaRef.current?.focus();
        }
        const messageKey =
          chatInputResolution.reason === 'unknown'
            ? 'viewModel.aiChat.skillCommandNotFound'
            : 'viewModel.aiChat.skillCommandSelectRequired';
        toast.error(t(messageKey));
        return;
      }

      // 送信時に初めてアップロードする（添付時アップロードだと送信キャンセルで孤児オブジェクトが残るため）。
      // 失敗した場合は入力・添付をそのまま残して中断する。画像・ファイルをまとめてアップロードする。
      let fileParts: FileUIPart[] = [];
      if (pendingImages.length > 0 || pendingFiles.length > 0) {
        try {
          fileParts = await Promise.all([
            ...pendingImages.map((attachment) =>
              uploadChatImageAttachment(attachment, { groupId, viewModelId, userId, threadId })
            ),
            ...pendingFiles.map((attachment) =>
              uploadChatFileAttachment(attachment, { groupId, viewModelId, userId, threadId })
            ),
          ]);
        } catch (error) {
          console.error('Failed to upload AI chat attachments', error);
          toast.error(t('viewModel.aiChat.attachmentUploadFailed'));
          return;
        }
      }

      if (currentInputRef.current === lastInput) {
        currentInputRef.current = '';
        setState((prev) => (prev.input === lastInput ? { ...prev, input: '' } : prev));
        setForcedSkillCommandCandidates(null);
      }
      clearImageAttachments();
      clearFileAttachments();
      // 送信時は続きを見たい意図が明確なので追従を強制復帰
      stickToBottom();
      // 送信のたびに解除する。解除しないと以降まったく編集ツールを実行できなくなる。
      editToolFailureGuard.reset();
      isStoppedRef.current = false;
      if (messages.length === 0) {
        await addInitialPrompt();
      }

      if (chatInputResolution.type === 'skillCommand') {
        const metadata: AIAssistantSkillCommandMetadata = {
          skillCommand: {
            name: chatInputResolution.skillName,
            args: chatInputResolution.args,
          },
        };
        if (fileParts.length > 0) {
          await sendMessage({ text: lastInput, files: fileParts, metadata });
        } else {
          await sendMessage({ text: lastInput, metadata });
        }
      } else if (lastInput.trim().length === 0) {
        // テキストなし・添付のみの送信
        await sendMessage({ files: fileParts });
      } else if (fileParts.length > 0) {
        await sendMessage({ text: lastInput, files: fileParts });
      } else {
        await sendMessage({ text: lastInput });
      }
    } catch {
      setState((prev) => (prev.input === '' ? { ...prev, input: lastInput } : prev));
      toast.error(t('viewModel.aiChat.toastSendError'));
    } finally {
      setIsSending(false);
    }
  };

  return (
    <>
      {pendingSkillChangeConfirmation && (
        <AIAssistantSkillChangeConfirmModal
          prepared={pendingSkillChangeConfirmation}
          onConfirm={() => closeSkillChangeConfirmation(true)}
          onCancel={() => closeSkillChangeConfirmation(false)}
        />
      )}
      <div className="flex items-start gap-2 py-3">
        <DarkTooltip tooltip={t('viewModel.aiChat.backLabel')} placement="bottom">
          <button
            className="text-sm text-gray-700 hover:text-gray-900"
            aria-label={t('viewModel.aiChat.backLabel')}
            onClick={() => {
              router.back();
            }}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
        </DarkTooltip>
        <div className="grow" />
        <DarkTooltip tooltip={t('viewModel.aiChat.newChat')} placement="bottom">
          <button
            className="px-1 text-sm text-gray-700 hover:text-gray-900"
            aria-label={t('viewModel.aiChat.newChat')}
            onClick={startNewChat}
          >
            <FontAwesomeIcon icon={faPlus} />
          </button>
        </DarkTooltip>
        {history.status === 'ready' && (
          <div className="relative">
            <button
              className="px-1 text-sm text-gray-700 hover:text-gray-900"
              aria-label={t('viewModel.aiChat.menuLabel')}
              onClick={openMenu}
            >
              <FontAwesomeIcon icon={faEllipsisV} />
            </button>
            <div
              ref={menuPopupRef}
              className={
                'absolute right-0 z-10 mt-1 w-48 rounded-lg bg-white py-1 shadow-lg ' +
                (isMenuOpen ? 'block' : 'hidden')
              }
            >
              <button
                className="block w-full px-2 py-1 text-left text-red-700 hover:bg-indigo-500 hover:text-white"
                onClick={() => {
                  closeMenu();
                  setIsDeleteModalOpen(true);
                }}
              >
                <span className="inline-block w-6 text-center">
                  <FontAwesomeIcon icon={faTrashAlt} />
                </span>
                <span className="text-sm">{t('viewModel.aiChat.deleteChatLabel')}</span>
              </button>
            </div>
          </div>
        )}
      </div>
      <AIAssistantChatDeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onConfirm={handleDeleteChat}
        onClose={() => setIsDeleteModalOpen(false)}
      />
      {history.status !== 'ready' ? (
        <div className="flex grow items-center justify-center py-8 text-sm text-gray-500">
          {t('viewModel.aiChat.loadingHistory')}
        </div>
      ) : (
        // チャットパネル全体（メッセージ一覧＋入力欄）を添付ファイルのドロップ領域にする
        <div className="relative flex min-h-0 grow flex-col" {...getDropzoneRootProps()}>
          <input {...getDropzoneInputProps()} />
          {isDragActive && (
            <div className="pointer-events-none absolute inset-0 z-20 m-1 flex flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-blue-400 bg-blue-50/80 px-4 text-center text-blue-600">
              <span className="text-sm font-medium">{t('viewModel.aiChat.attachmentDropHint')}</span>
              <span className="text-xs text-blue-500">
                {t('viewModel.aiChat.attachmentDropConstraints', {
                  max: MAX_TOTAL_CHAT_ATTACHMENTS,
                  fileMax: MAX_CHAT_FILE_ATTACHMENTS,
                })}
              </span>
            </div>
          )}
          <div ref={scrollContainerRef} onScroll={onScroll} className="-mx-4 max-h-full grow overflow-auto px-4">
            <div ref={scrollContentRef} className="flex flex-col gap-2">
              {messages.map((message) => (
                <AIChatMessageRow key={message.id} message={message} debugEnabled={messageDebugEnabled} />
              ))}

              {status === 'submitted' && <LoadingIndicator>{t('viewModel.aiChat.submitting')}</LoadingIndicator>}
              {status === 'streaming' && <LoadingIndicator>{t('viewModel.aiChat.streaming')}</LoadingIndicator>}
              {status === 'error' && (
                <div className="w-max rounded-lg bg-red-50 px-2 py-1 text-xs text-red-700">
                  {t('viewModel.aiChat.errorFallback')}
                </div>
              )}
            </div>
          </div>

          <div className="relative py-2">
            {isOverAIUsageLimit && <AIUsageLimitReachedNotice groupId={groupId} className="mb-2" />}
            {isNearAIUsageLimit && aiUsageStatus && (
              <div className="mb-2 rounded border border-yellow-700 bg-yellow-200 p-2 text-xs">
                {t('framework.aiUsage.warningMessage', { percent: Math.floor(aiUsageStatus.usedRatio * 100) })}
              </div>
            )}
            {isSkillCommandMenuOpen && (
              <AIAssistantSkillCommandMenu
                candidates={visibleSkillCommandCandidates}
                highlightedIndex={highlightedSkillCommandIndex}
                label={t('viewModel.aiChat.skillCommandSuggestionsLabel')}
                argumentLabel={t('viewModel.aiChat.skillCommandArgumentLabel')}
                onHighlight={setHighlightedSkillCommandIndex}
                onSelect={selectSkillCommand}
              />
            )}
            {/* テキストボックスっぽく見せる境界 */}
            <div
              className={classNames(
                'flex w-full flex-col gap-2 rounded border bg-gray-100 p-1',
                isDragActive ? 'border-blue-400' : 'border-gray-300 focus-within:border-blue-500/50 focus-within:ring-1'
              )}
            >
              {totalAttachmentCount > 0 && (
                <div className="flex w-full flex-wrap items-center gap-2">
                  {attachments.map((attachment) => (
                    <ChatImageAttachmentPreview
                      key={attachment.id}
                      attachment={attachment}
                      onRemove={() => removeImageAttachment(attachment.id)}
                    />
                  ))}
                  {fileAttachments.map((attachment) => (
                    <ChatFileChip
                      key={attachment.id}
                      filename={attachment.filename}
                      mediaType={attachment.mediaType}
                      sizeBytes={attachment.sizeBytes}
                      onRemove={() => removeFileAttachment(attachment.id)}
                    />
                  ))}
                </div>
              )}
              <textarea
                ref={textareaRef}
                key={state.sessionId}
                autoFocus
                placeholder={t('viewModel.aiChat.placeholder')}
                value={state.input}
                aria-autocomplete="list"
                aria-expanded={isSkillCommandMenuOpen}
                aria-controls={isSkillCommandMenuOpen ? AI_ASSISTANT_SKILL_COMMAND_LISTBOX_ID : undefined}
                aria-activedescendant={
                  isSkillCommandMenuOpen ? getSkillCommandOptionId(highlightedSkillCommandIndex) : undefined
                }
                className="max-h-40 w-full resize-none overflow-y-auto bg-transparent p-0 text-sm focus:outline-none"
                style={
                  {
                    fieldSizing: 'content',
                  } as CSSProperties
                }
                onChange={(event) => {
                  const value = event.target.value;
                  currentInputRef.current = value;
                  setForcedSkillCommandCandidates(null);
                  setDismissedSkillCommandInput(null);
                  setState((prev) => ({ ...prev, input: value }));
                }}
                onFocus={() => setIsInputFocused(true)}
                onBlur={() => setIsInputFocused(false)}
                onPaste={(event) => {
                  const imageFiles = extractPastedImageFiles(event.clipboardData);
                  if (imageFiles.length === 0) return;
                  // 画像のみの貼り付け（スクリーンショット等）はテキスト挿入を伴わないため既定動作を止める。
                  // テキストも含む場合（表計算セルのコピー等）はテキストの貼り付けを妨げない。
                  if (event.clipboardData.getData('text/plain').length === 0) {
                    event.preventDefault();
                  }
                  // 上限超過・非対応形式は addImageAttachments 側でトースト通知される
                  void addImageAttachments(imageFiles);
                }}
                onKeyDown={async (event) => {
                  if (event.nativeEvent.isComposing) return;

                  if (event.key === 'Enter' && event.ctrlKey) {
                    // 改行の挿入を防止（送信時）
                    event.preventDefault();
                    // 応答中は入力のみ許可し、送信は送信可能な状態でのみ行う
                    if (canSend) {
                      await sendCurrentInput();
                    }
                    return;
                  }

                  if (!isSkillCommandMenuOpen) return;

                  if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                    event.preventDefault();
                    const direction = event.key === 'ArrowDown' ? 1 : -1;
                    setHighlightedSkillCommandIndex(
                      (highlightedSkillCommandIndex + direction + visibleSkillCommandCandidates.length) %
                        visibleSkillCommandCandidates.length
                    );
                  } else if (event.key === 'Enter' || event.key === 'Tab') {
                    event.preventDefault();
                    const candidate = visibleSkillCommandCandidates[highlightedSkillCommandIndex];
                    if (candidate) selectSkillCommand(candidate);
                  } else if (event.key === 'Escape') {
                    event.preventDefault();
                    setForcedSkillCommandCandidates(null);
                    setDismissedSkillCommandInput(state.input);
                  }
                }}
              />
              <div className="flex w-full items-center justify-between gap-1">
                <DarkTooltip
                  tooltip={
                    <span className="block text-center">
                      {t('viewModel.aiChat.attachAttachmentLabel')}
                      <br />
                      {t('viewModel.aiChat.attachmentDropConstraints', {
                        max: MAX_TOTAL_CHAT_ATTACHMENTS,
                        fileMax: MAX_CHAT_FILE_ATTACHMENTS,
                      })}
                    </span>
                  }
                  placement="top"
                >
                  <button
                    type="button"
                    aria-label={t('viewModel.aiChat.attachAttachmentLabel')}
                    onClick={openFilePicker}
                    disabled={totalAttachmentCount >= MAX_TOTAL_CHAT_ATTACHMENTS || isProcessingAttachments}
                    className="flex size-7 items-center justify-center rounded text-gray-600 hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <FontAwesomeIcon icon={faPaperclip} />
                  </button>
                </DarkTooltip>
                <CustomSelect
                  value={state.model}
                  options={modelSelectOptions}
                  onChange={(modelId) => {
                    AIAssistantSettingsStorage.setSelectedModel(modelId);
                    modelRef.current = modelId;
                    setState((prev) => ({ ...prev, model: modelId }));
                  }}
                  openDirection="up"
                  align="right"
                  triggerClassName="flex w-full items-center justify-between gap-1 rounded border border-gray-400 bg-white py-0.5 pl-2 pr-2 text-sm text-gray-700 hover:border-gray-500 focus:outline-none"
                  menuClassName="max-h-72 w-72 overflow-y-auto"
                />
              </div>
            </div>
            <div className="mt-1 flex items-center gap-2">
              <ModeSelect
                value={state.mode}
                onChange={(nextMode) => {
                  modeRef.current = nextMode;
                  AIAssistantSettingsStorage.setSelectedMode(nextMode);
                  setState((prev) => ({ ...prev, mode: nextMode }));
                }}
              />
              {selectedViewLabel && (
                <div className="min-w-0 flex-1 truncate text-xs text-gray-500">
                  {t('viewModel.aiChat.selectedView', { name: selectedViewLabel })}
                </div>
              )}
              {isStreaming ? (
                <button
                  type="button"
                  onClick={async () => {
                    isStoppedRef.current = true;
                    await stop();
                    // 停止時点で実行途中だった tool part を中断扱いに確定させる
                    finalizeInterruptedToolParts();
                  }}
                  className="ml-auto inline-flex items-center gap-1 rounded border border-gray-300 bg-gray-50 px-2 py-0.5 text-xs text-gray-700 hover:bg-gray-100"
                  title={t('viewModel.aiChat.stopTitle')}
                >
                  {t('viewModel.aiChat.stop')}
                </button>
              ) : (
                <button
                  type="button"
                  disabled={!canSend}
                  onClick={async () => {
                    await sendCurrentInput();
                  }}
                  className="ml-auto inline-flex items-center gap-1 rounded border border-gray-300 bg-gray-50 px-2 py-0.5 text-xs text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                  title={t('viewModel.aiChat.sendTitle')}
                >
                  {t('common.action.send')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function useChatParamsSourceRef(source: ChatParamsSource): { readonly current: ChatParamsSource } {
  const ref = useRef<ChatParamsSource>(source);
  ref.current = source;
  return ref;
}

// 【この計算は AI SDK の内部実装に依存している】
// サードパーティライブラリに依存しており、コードとテストだけでは正しさを判断できないため、
// ai SDK のバージョン 7.0.107 時点のソースを精読したうえで、確認できた事実をここに記録する。
// ai SDK のバージョン更新時は以下の前提が崩れていないか SDK ソースで再確認すること。
//
// 【なぜ早期 resolve（tool 継続中に収束扱い）が起きないか】
// ai SDK はストリームの各 chunk を TransformStream で chunk ごとに await して順次処理する。
// tool-input-available chunk の処理中に onToolCall を await するため、
// onToolCall 内の addToolOutput（tool part を output-available/output-error に更新）が
// 完了するまで次の chunk に進まず、全 chunk 消費が完了してから status=ready に遷移する。
// https://github.com/vercel/ai/blob/08ae5ad05bc12496dd1ffcf64e34419e0831300d/packages/ai/src/ui/chat.ts#L561
//
// よって status=ready になる時点では全 client tool part が確定済みで、
// lastAssistantMessageIsCompleteWithToolCalls が true になり pending に倒れる。
//
// - ストリーム消費と ready 遷移の順序: https://github.com/vercel/ai/blob/08ae5ad05bc12496dd1ffcf64e34419e0831300d/packages/ai/src/ui/chat.ts#L807-L834
// - onToolCall の await: https://github.com/vercel/ai/blob/08ae5ad05bc12496dd1ffcf64e34419e0831300d/packages/ai/src/ui/process-ui-message-stream.ts#L668-L705
//
function isChatInflight(status: ChatStatus): boolean {
  return status === 'submitted' || status === 'streaming';
}

// 【条件 lastAssistantMessageIsCompleteWithToolCalls の意味】
// lastAssistantMessageIsCompleteWithToolCalls は「最終 step の全 client tool part（!providerExecuted）が
// output-available（preliminary を除く）または output-error」のとき true を返す。
// true の場合は SDK が直後に自動再送（status→submitted）するので、収束ではなく中間 ready である。
// https://github.com/vercel/ai/blob/08ae5ad05bc12496dd1ffcf64e34419e0831300d/packages/ai/src/ui/last-assistant-message-is-complete-with-tool-calls.ts#L7
//
// 【条件 lastMessage の意味と必要性】
// input-streaming/input-available の client tool part が存在するなら onToolCall が進行中で未収束。
// onToolCall が addToolOutput を呼ばずに return するケース（dynamic-tool 等）では
// tool part が input-available のまま status=ready になりうるため、
// lastAssistantMessageIsCompleteWithToolCalls だけでは漏れる。
// lastMessage の条件は、そのケースを pending に倒す安全網であり必須。
//
// 【state 4値の前提】
// tool part の state は input-streaming/input-available/output-available/output-error の4値のみを扱う。
// SDK には承認系の state（approval-requested/approval-responded/output-denied）もあるが、
// 承認フローを使っていないため到達しない。
// 中間の limbo 状態は存在しないため、条件 lastAssistantMessageIsCompleteWithToolCalls と
// 条件 lastMessage の組で全ての中間 ready を漏れなく弾ける。
// 将来 SDK が新しい state 値を追加した場合はこの前提が崩れるため要再検証。
//
// 【editToolFailureGuardTripped の意味】
// 打ち切り時は sendAutomaticallyWhen が自動再送を止めるため、
// lastAssistantMessageIsCompleteWithToolCalls が true でも次ターンは来ない。
// この条件だけで判定すると中間 ready と誤認し、MCPセッションがタイムアウトまで pending になる。
function isAIResponseComplete(messages: UIMessage[], editToolFailureGuardTripped: boolean): boolean {
  const lastMessage = messages.at(-1);
  const hasInflightClientToolPart =
    lastMessage?.role === 'assistant' &&
    (lastMessage.parts ?? []).some(
      (p) => isToolUIPart(p) && !p.providerExecuted && (p.state === 'input-streaming' || p.state === 'input-available')
    );
  if (hasInflightClientToolPart) return false;
  if (editToolFailureGuardTripped) return true;
  return !lastAssistantMessageIsCompleteWithToolCalls({ messages });
}
